"use client";

import { useMemo, useState } from "react";
import {
  Checkbox,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT, LABEL } from "@/components/tool-kit/styles";
import {
  CompactTextArea,
  CopyList,
  DraftPanel,
  FormHeader,
} from "@/components/tools/academic-correspondence/kit";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";
import { countPlaceholders } from "@/lib/tool-kit/correspondence/compose";
import {
  buildProfessorEmail,
  COURSE_REQUIRED,
  type DetailSpec,
  type EmailTone,
  PROFESSOR_EMAIL_BANK,
  PROFESSOR_EMAIL_DETAILS,
  PROFESSOR_EMAIL_PURPOSES,
  PROFESSOR_TITLES,
  type ProfessorEmailPurpose,
  type ProfessorTitle,
  professorEmailText,
} from "@/lib/tool-kit/correspondence/email-to-professor";

/** English strings. A translated content file overrides these through `ui`. */
export const EMAIL_TO_PROFESSOR_GENERATOR_STRINGS = {
  formHeading: "Your email",
  tryExample: "Try an example",
  clear: "Clear",
  purposeLabel: "What are you writing about?",
  purposes: {
    extension: "Asking for an extension",
    absence: "Explaining an absence",
    question: "A question about course material",
    meeting: "Requesting a meeting or office hours",
    recommendation: "Asking for a recommendation letter",
    grade: "Asking about a grade",
    research: "Asking about research or RA opportunities",
    introduction: "Introducing yourself",
  },
  titleLabel: "How to address them",
  titleHint:
    "Use the title on their syllabus, staff page, or email signature. Unsure? See “Professor or Dr.?” below.",
  titleOptions: {
    professor: "Professor",
    dr: "Dr.",
    drNoStop: "Dr (no full stop, British style)",
    ms: "Ms.",
    mr: "Mr.",
    mx: "Mx.",
  },
  recipientLabel: "Their family name",
  recipientPlaceholder: "Okafor",
  recipientHint:
    "As they write it. If you are not sure which part is the family name, enter the full name.",
  courseHint: "The course code is usually enough.",
  courseLabel: "Course",
  courseOptionalLabel: "Course (optional)",
  coursePlaceholder: "ECON 210",
  yourNameLabel: "Your full name",
  yourNamePlaceholder: "Priya Raman",
  studentIdLabel: "Student ID (optional)",
  studentIdPlaceholder: "20481937",
  toneLabel: "Tone",
  tones: {
    formal: "Formal",
    friendly: "Friendly but professional",
  },
  detailsHeading: "The specifics",
  detailLabels: {
    assignment: "Assignment",
    dueDate: "Current deadline",
    newDate: "New deadline you are asking for",
    reason: "Brief reason",
    progress: "What you have done so far (optional)",
    documentation: "I can provide supporting documentation",
    absenceDate: "Class you missed or will miss",
    upcoming: "I am writing before the class, not after it",
    absenceReason: "Brief reason (optional)",
    catchUp: "How you are catching up (optional)",
    missedWork: "Assessed work you missed (optional)",
    topic: "Topic or reading",
    question: "Your question",
    tried: "What you have already tried (optional)",
    meetingTopic: "What you would like to discuss",
    meetingTimes: "When you are free",
    program: "What you are applying for",
    deadline: "Deadline for the letter",
    connection: "How they know your work",
    why: "Why you are asking them (optional)",
    materials: "What you will send to help (optional)",
    grade: "Grade you received (optional)",
    understand: "What you would like to understand",
    theirWork: "What interests you about their research",
    background: "Your relevant background",
    hours: "Time you could commit (optional)",
    cv: "I am attaching my CV",
    context: "Why you are writing",
    aboutYou: "A little about you (optional)",
    ask: "Anything you would like to ask (optional)",
  },
  detailExamples: {
    assignment: "the research essay",
    dueDate: "Friday 14 March",
    newDate: "Monday 17 March",
    reason: "a family emergency that took me out of town this week",
    progress: "I have finished the research and about half of the draft",
    absenceDate: "Tuesday's lecture (11 March)",
    absenceReason: "a medical appointment",
    catchUp:
      "I have borrowed notes from a classmate and watched the lecture recording",
    missedWork: "the in-class quiz",
    topic: "the week 4 reading on comparative advantage",
    question:
      "In the example on page 82, why does the country with the lower opportunity cost export the good, even though the other country is more productive?",
    tried: "re-read chapter 3 and the lecture slides",
    meetingTopic: "my plan for the term paper",
    meetingTimes: "on Tuesday after 2 pm or on Thursday morning",
    program: "the MSc in Global Health at the University of Cape Town",
    deadline: "1 December",
    connection:
      "I took ECON 210 with you last autumn, and my term paper was on microfinance in Kenya.",
    why: "Your feedback on that paper is a big part of why I want to study development economics.",
    materials: "my CV, my personal statement, and the submission link",
    grade: "62%",
    understand: "how I could have structured my argument more effectively",
    theirWork: "your recent work on mobile money and household savings",
    background:
      "I am a third-year economics student, and I have used R and Stata in two course projects.",
    hours: "about 10 hours a week from January",
    context: "I will be joining your Development Economics seminar next term",
    aboutYou:
      "I am a third-year economics student with a particular interest in inequality.",
    ask: "whether there is any reading you would recommend before term starts",
  },
  detailsHint:
    "Short phrases work best: most of these slot into a sentence, so start in lowercase and leave off the full stop.",
  privacyHint:
    "A short phrase is enough. You do not have to share medical or other private details.",
  draftHeading: "Your draft",
  subjectsHeading: "Subject lines",
  alternativesHeading: "Other ways to make the request",
  copy: "Copy",
  copyPlain: "Copy plain text",
  copied: "Copied!",
  download: "Download .txt",
  downloadFilename: "email-to-professor.txt",
  placeholdersLeft:
    "one:{count} placeholder left to fill — shown in [brackets]|other:{count} placeholders left to fill — shown in [brackets]",
  noPlaceholders:
    "No placeholders left. Read it through once more before you send it.",
  example: {
    title: "professor",
    recipient: "Okafor",
    course: "ECON 210",
    yourName: "Priya Raman",
    studentId: "20481937",
  },
  ...PROFESSOR_EMAIL_BANK,
} as const;

type Strings = typeof EMAIL_TO_PROFESSOR_GENERATOR_STRINGS;

/** The yes/no details the example ticks. Values, not prose. */
const EXAMPLE_TICKED: Readonly<Record<string, string>> = {
  documentation: "yes",
  cv: "yes",
};

/** A reason for an absence or an extension is nobody's business in detail. */
const PRIVATE_DETAILS = new Set(["reason", "absenceReason"]);

function DetailField({
  spec,
  value,
  onChange,
  strings,
}: {
  spec: DetailSpec;
  value: string;
  onChange: (value: string) => void;
  strings: Strings;
}) {
  const label =
    strings.detailLabels[spec.id as keyof Strings["detailLabels"]] ?? spec.id;
  const example =
    strings.detailExamples[spec.id as keyof Strings["detailExamples"]];
  if (spec.shape === "checkbox") {
    return (
      <div className="sm:col-span-2">
        <Checkbox
          checked={value === "yes"}
          label={label}
          onChange={(checked) => onChange(checked ? "yes" : "")}
        />
      </div>
    );
  }
  if (spec.multiline) {
    return (
      <div className="sm:col-span-2">
        <CompactTextArea
          id={`etp-${spec.id}`}
          label={label}
          onChange={onChange}
          placeholder={example}
          rows={3}
          value={value}
        />
      </div>
    );
  }
  return (
    <TextField
      hint={PRIVATE_DETAILS.has(spec.id) ? strings.privacyHint : undefined}
      id={`etp-${spec.id}`}
      label={label}
      onChange={onChange}
      placeholder={example}
      value={value}
    />
  );
}

const PURPOSE_KEYS: readonly ProfessorEmailPurpose[] = PROFESSOR_EMAIL_PURPOSES;

export default function EmailToProfessorGeneratorWidget() {
  const strings = useToolUi(EMAIL_TO_PROFESSOR_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [purpose, setPurpose] = useState<ProfessorEmailPurpose>("extension");
  const [title, setTitle] = useState<ProfessorTitle>("professor");
  const [tone, setTone] = useState<EmailTone>("formal");
  const [recipient, setRecipient] = useState("");
  const [course, setCourse] = useState("");
  const [yourName, setYourName] = useState("");
  const [studentId, setStudentId] = useState("");
  const [details, setDetails] = useState<Record<string, string>>({});

  const draft = useMemo(
    () =>
      buildProfessorEmail(
        {
          purpose,
          tone,
          title,
          recipient,
          course,
          yourName,
          studentId,
          details,
        },
        strings
      ),
    [
      purpose,
      tone,
      title,
      recipient,
      course,
      yourName,
      studentId,
      details,
      strings,
    ]
  );

  const blanks = countPlaceholders(draft.paragraphs);
  const status =
    blanks > 0
      ? tPlural(strings.placeholdersLeft, locale, blanks, {
          count: fmt(blanks),
        })
      : strings.noPlaceholders;

  function fillExample() {
    const example = strings.example;
    setTitle(example.title as ProfessorTitle);
    setRecipient(example.recipient);
    setCourse(example.course);
    setYourName(example.yourName);
    setStudentId(example.studentId);
    setDetails({ ...strings.detailExamples, ...EXAMPLE_TICKED });
  }

  function clearAll() {
    setRecipient("");
    setCourse("");
    setYourName("");
    setStudentId("");
    setDetails({});
  }

  const specs = PROFESSOR_EMAIL_DETAILS[purpose];

  return (
    <>
      <section className="space-y-5">
        <FormHeader
          clearLabel={strings.clear}
          exampleLabel={strings.tryExample}
          heading={strings.formHeading}
          onClear={clearAll}
          onExample={fillExample}
        />
        <SelectField
          id="etp-purpose"
          label={strings.purposeLabel}
          onChange={setPurpose}
          options={PURPOSE_KEYS.map((key) => ({
            value: key,
            label: strings.purposes[key],
          }))}
          value={purpose}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            hint={strings.titleHint}
            id="etp-title"
            label={strings.titleLabel}
            onChange={setTitle}
            options={PROFESSOR_TITLES.map((key) => ({
              value: key,
              label: strings.titleOptions[key],
            }))}
            value={title}
          />
          <TextField
            hint={strings.recipientHint}
            id="etp-recipient"
            label={strings.recipientLabel}
            onChange={setRecipient}
            placeholder={strings.recipientPlaceholder}
            value={recipient}
          />
          <TextField
            hint={strings.courseHint}
            id="etp-course"
            label={
              COURSE_REQUIRED[purpose]
                ? strings.courseLabel
                : strings.courseOptionalLabel
            }
            onChange={setCourse}
            placeholder={strings.coursePlaceholder}
            value={course}
          />
          <TextField
            id="etp-your-name"
            label={strings.yourNameLabel}
            onChange={setYourName}
            placeholder={strings.yourNamePlaceholder}
            value={yourName}
          />
          <TextField
            id="etp-student-id"
            label={strings.studentIdLabel}
            onChange={setStudentId}
            placeholder={strings.studentIdPlaceholder}
            value={studentId}
          />
        </div>
        <PillGroup
          label={strings.toneLabel}
          name="etp-tone"
          onChange={setTone}
          options={[
            { value: "formal", label: strings.tones.formal },
            { value: "friendly", label: strings.tones.friendly },
          ]}
          value={tone}
        />
        <fieldset className="space-y-4">
          <legend className={LABEL}>{strings.detailsHeading}</legend>
          <p className={HINT}>{strings.detailsHint}</p>
          <div className="grid gap-4 sm:grid-cols-2">
            {specs.map((spec) => (
              <DetailField
                key={spec.id}
                onChange={(value) =>
                  setDetails((current) => ({ ...current, [spec.id]: value }))
                }
                spec={spec}
                strings={strings}
                value={details[spec.id] ?? ""}
              />
            ))}
          </div>
        </fieldset>
      </section>

      <DraftPanel
        download={professorEmailText(draft, strings)}
        filename={strings.downloadFilename}
        heading={strings.draftHeading}
        intro={
          <CopyList
            copiedLabel={strings.copied}
            copyLabel={strings.copy}
            heading={strings.subjectsHeading}
            items={draft.subjects}
          />
        }
        paragraphs={draft.paragraphs}
        status={status}
        strings={strings}
      >
        <CopyList
          copiedLabel={strings.copied}
          copyLabel={strings.copy}
          heading={strings.alternativesHeading}
          items={draft.alternatives}
        />
      </DraftPanel>
    </>
  );
}
